import React from 'react';
import { X } from 'lucide-react';
import { Score } from '../types';
import { GolfCourse } from '../types';

interface PlayerScoreBreakdownProps {
  playerName: string;
  scores: Score[];
  tournamentId: string;
  onClose: () => void;
  course: GolfCourse;
}

export function PlayerScoreBreakdown({ playerName, scores, tournamentId, onClose, course }: PlayerScoreBreakdownProps) {
  // Filter scores for this tournament only
  const tournamentScores = scores.filter(score => score.tournamentId === tournamentId);
  
  // Convert to record format for easier display
  const scoreRecord: Record<number, number> = {};
  tournamentScores.forEach(score => {
    scoreRecord[score.hole] = score.strokes;
  });

  const totalScore = Object.values(scoreRecord).reduce((sum, score) => sum + score, 0);
  
  // Calculate OUT (front 9) total
  const outTotal = [1,2,3,4,5,6,7,8,9].reduce((sum, hole) => sum + (scoreRecord[hole] || 0), 0);
  
  // Calculate IN (back 9) total
  const inTotal = [10,11,12,13,14,15,16,17,18].reduce((sum, hole) => sum + (scoreRecord[hole] || 0), 0);

  // Function to get score highlight class
  const getScoreClass = (score: number | undefined, par: number) => {
    if (!score) return '';
    const diff = score - par;
    if (diff <= -2) return 'bg-yellow-100'; // Eagle or better
    if (diff === -1) return 'bg-green-100'; // Birdie
    if (diff === 1) return 'bg-red-100'; // Bogey
    if (diff >= 2) return 'bg-blue-100'; // Double bogey or worse
    return '';
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-4 sm:p-6 max-w-2xl w-full mx-4 overflow-x-auto">
        <div className="flex justify-between items-center mb-4 sm:mb-6">
          <h3 className="text-lg font-semibold">{playerName}'s Scores</h3>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Front 9 */}
        <div className="mb-4">
          <div className="grid grid-cols-10 gap-2 sm:gap-3 mb-1">
            {[1,2,3,4,5,6,7,8,9].map(hole => (
              <div key={hole} className="text-center text-sm font-medium text-gray-600 min-w-[2rem]">
                {hole}
                <div className="text-xs text-gray-400">{course.parValues[hole - 1]}</div>
              </div>
            ))}
            <div className="text-center text-sm font-medium text-gray-600 min-w-[2rem]">OUT</div>
          </div>
          <div className="grid grid-cols-10 gap-2 sm:gap-3">
            {[1,2,3,4,5,6,7,8,9].map(hole => (
              <div 
                key={hole} 
                className={`text-center font-medium min-w-[2rem] text-gray-900 p-1 rounded ${getScoreClass(scoreRecord[hole], course.parValues[hole - 1])}`}
              >
                {scoreRecord[hole] || '-'}
              </div>
            ))}
            <div className="text-center font-medium min-w-[2rem] text-gray-900">{outTotal || '-'}</div>
          </div>
        </div>

        {/* Back 9 */}
        <div className="mb-4">
          <div className="grid grid-cols-10 gap-2 sm:gap-3 mb-1">
            {[10,11,12,13,14,15,16,17,18].map(hole => (
              <div key={hole} className="text-center text-sm font-medium text-gray-600 min-w-[2rem]">
                {hole}
                <div className="text-xs text-gray-400">{course.parValues[hole - 1]}</div>
              </div>
            ))}
            <div className="text-center text-sm font-medium text-gray-600 min-w-[2rem]">IN</div>
          </div>
          <div className="grid grid-cols-10 gap-2 sm:gap-3">
            {[10,11,12,13,14,15,16,17,18].map(hole => (
              <div 
                key={hole} 
                className={`text-center font-medium min-w-[2rem] text-gray-900 p-1 rounded ${getScoreClass(scoreRecord[hole], course.parValues[hole - 1])}`}
              >
                {scoreRecord[hole] || '-'}
              </div>
            ))}
            <div className="text-center font-medium min-w-[2rem] text-gray-900">{inTotal || '-'}</div>
          </div>
        </div>

        {/* Total */}
        <div className="mt-4 text-right">
          <span className="font-medium text-lg text-gray-900">
            {totalScore} Total
          </span>
        </div>
      </div>
    </div>
  );
} 